/**
 * Assessment Performance — Redux slice.
 *
 * Owns:
 *   - Period A / Period B date ranges + active preset id
 *   - Account filter (multi-select objects: `[{ uuid, name }]`).
 *     Storing the display name alongside the uuid lets the chip
 *     label show "Mercy Health Partners" without round-tripping
 *     through the picker's catalogue cache.
 *   - Comparison metrics (rows + totals) — these double as the
 *     drilldown source: when the user clicks a comparison-table
 *     row, the modal reads its data from the matching `rows[i]`
 *     entry instead of issuing a second fetch.
 *   - Request-status flags for the metrics fetch.
 *
 * The Account picker uses `useFetchList` against the existing
 * `/accounts/{uuid}/account-lookups` endpoint and owns its own
 * paging cache locally — the slice never touches that data.
 */
import { createSlice } from '@reduxjs/toolkit';
import { REQUEST_STATUS } from '../constants';
import { fetchAssessmentMetrics } from '../api';
import {
  PRESET_IDS,
  buildPresetRanges,
} from '../../entities/assessment-performance/lib/buildPresetRange';

// Default preset = week-over-week per spec section 6.2.
const defaultPreset = buildPresetRanges(PRESET_IDS.weekOverWeek);

const initialState = {
  // Filters
  periodA: defaultPreset?.periodA ?? { startDate: null, endDate: null },
  periodB: defaultPreset?.periodB ?? { startDate: null, endDate: null },
  preset: PRESET_IDS.weekOverWeek,
  selectedAccounts: [],
  // What we ask the API for. The default is false: automated
  // assessments are excluded unless the user opts in.
  includeAutomated: false,

  // Metrics for the comparison table + charts
  rows: [],
  totals: null,
  generatedAt: null,
  // What the API says the current numbers actually include. Rendered
  // state comes from here, not from `includeAutomated` above.
  includesAutomated: false,
  metricsStatus: REQUEST_STATUS.idle,
  metricsError: null,

  // Drilldown — only the open uuid lives in state; the breakdown
  // itself is read from `rows` (no second fetch).
  selectedAccountUuid: null,
};

const assessmentPerformanceSlice = createSlice({
  name: 'assessmentPerformance',
  initialState,
  reducers: {
    setPeriodA: (state, action) => {
      state.periodA = action.payload;
    },
    setPeriodB: (state, action) => {
      state.periodB = action.payload;
    },
    setPreset: (state, action) => {
      const id = action.payload;
      state.preset = id;
      const ranges = buildPresetRanges(id);
      if (ranges) {
        state.periodA = ranges.periodA;
        state.periodB = ranges.periodB;
      }
    },
    setSelectedAccounts: (state, action) => {
      state.selectedAccounts = action.payload || [];
    },
    setSelectedAccountUuid: (state, action) => {
      state.selectedAccountUuid = action.payload;
    },
    setIncludeAutomated: (state, action) => {
      state.includeAutomated = Boolean(action.payload);
    },
    resetAssessmentPerformance: () => ({ ...initialState }),
  },
  extraReducers(builder) {
    builder
      .addCase(fetchAssessmentMetrics.pending, (state) => {
        state.metricsStatus = REQUEST_STATUS.loading;
        state.metricsError = null;
      })
      .addCase(fetchAssessmentMetrics.fulfilled, (state, action) => {
        if (action.payload.ok) {
          state.metricsStatus = REQUEST_STATUS.succeeded;
          state.rows = action.payload.data.rows;
          state.totals = action.payload.data.totals;
          state.generatedAt = action.payload.data.generatedAt || null;
          state.includesAutomated = action.payload.data.includesAutomated;
        } else {
          state.metricsStatus = REQUEST_STATUS.failed;
          state.metricsError =
            action.payload.error?.message || 'Failed to load metrics';
        }
      })
      .addCase(fetchAssessmentMetrics.rejected, (state, action) => {
        state.metricsStatus = REQUEST_STATUS.failed;
        state.metricsError =
          action.payload?.error?.message ||
          action.error?.message ||
          'Failed to load metrics';
      });
  },
});

export const {
  setPeriodA,
  setPeriodB,
  setPreset,
  setSelectedAccounts,
  setSelectedAccountUuid,
  setIncludeAutomated,
  resetAssessmentPerformance,
} = assessmentPerformanceSlice.actions;

export default assessmentPerformanceSlice.reducer;
